import React, { useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Input, TextArea } from '@repo/ui'
import { useSnackbar } from 'contexts'
import { useCreateSupportRequest } from 'hooks/api'

/**
 * VRG-266 / VERA-246 — "we can make that" intake for the tag shop.
 *
 * Files an ordinary SUPPORT REQUEST carrying `topic: 'custom_tag'`, rather than a board
 * item. A custom-tag enquiry needs a threaded back-and-forth to scope and quote, which
 * support has and the suggestion board (built for voting and demand-counting) does not.
 * It also means the enquiry lands in the brand's own Support list, so the submitter can
 * track it — an email would simply vanish.
 *
 * `topic` rather than `type`: the schema documents `type` as "a discriminator, not a
 * classification", and folding this into it would have left the demand uncountable
 * without grepping ticket bodies.
 *
 * Notification comes for free. `SupportService.createRequest` calls
 * `SupportNotifierService.requestCreated`, which emails the shared internal inbox
 * (`supportInternalEmail`, default `support@vera.inc`) on every new request. Nothing
 * topic-specific is wired, deliberately — see the note in `config/app.ts`, which records
 * that per-company manager routing was attempted and abandoned because no usable
 * manager↔company mapping exists.
 */
export const CustomTagModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { open: notification } = useSnackbar()
  const { mutateAsync, isPending } = useCreateSupportRequest()

  const [need, setNeed] = useState('')
  const [quantity, setQuantity] = useState('')

  const canSubmit = need.trim().length > 0

  const reset = () => {
    setNeed('')
    setQuantity('')
  }

  const submit = async () => {
    if (!canSubmit) return
    // Quantity is a separate field for the submitter's benefit (it is the question they
    // would otherwise be asked back), but it is composed into the body rather than stored
    // on its own column: one freeform enquiry does not justify schema, and support already
    // renders the body as the thread's first message.
    const body = [
      'Custom tag enquiry (from the tag shop)',
      '',
      need.trim(),
      ...(quantity.trim() ? ['', `Rough quantity: ${quantity.trim()}`] : []),
    ].join('\n')

    try {
      await mutateAsync({
        type: 'support',
        topic: 'custom_tag',
        body,
        // Context, captured rather than typed — same contract as every other intake.
        pageUrl: window.location.pathname + window.location.search,
      })
      notification({ type: 'success', title: "Thanks. We'll come back to you with options." })
      reset()
      onClose()
    } catch (e: any) {
      // Keeps the form open with the text intact, so a retry does not mean retyping.
      notification({ type: 'error', title: e?.message || 'Could not send your enquiry. Please try again.' })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Need something custom?"
      actions={
        <>
          {/* Cancel stays enabled — it is the exit, not a discard (§54). */}
          <Button variant="secondary" disableElevation onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disableElevation
            onClick={submit}
            loading={isPending}
            disabled={!canSubmit}
          >
            Send enquiry
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px', lineHeight: 1.5, pb: '20px' }}>
          We can manufacture bespoke tags. Tell us what you need and we'll come back with
          options and pricing.
        </Typography>

        <Stack spacing={1}>
          <Typography variant="body3">What do you need?</Typography>
          {/* "What product this is for" leads, because it is the most orienting answer:
              it tells us the use case, which usually implies half the other constraints.
              A textarea placeholder may wrap, so the list can be this long without
              truncating the way it would in a single-line input. */}
          <TextArea
            minRows={4}
            placeholder="What product this is for, material, size, shape, capability, where it needs to go, anything else that matters"
            value={need}
            onChange={(v) => setNeed(v)}
          />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="body3">Rough quantity (optional)</Typography>
          <Input placeholder="e.g. 5,000 per year" value={quantity} onChange={(v) => setQuantity(v)} />
        </Stack>
      </Stack>
    </Modal>
  )
}
